

































































import {
  defineComponent,
  reactive,
  SetupContext,
  Ref,
} from '@vue/composition-api'
import moment from 'moment'

import { UserState } from '@/types/store'
import { IItem, IReceipt, ItemType } from '@/types/receipt'
import { IRecommend } from '@/types/recommend'
import { IMatched } from '@/types/matched'
import { IUser } from '@/types/user'
import { ApprovalResult, LogType } from '@/types/log'

import useVuex from '@/utils/useVuex'

import { ReceiptView } from '@/components/molecules'
import { MasterAutocomplete } from '@/components/atoms'

import { getRecommends } from '@/api/recommend'
import { getReceipt } from '@/api/receipts'
import { putItem, deleteItem } from '@/api/items'

type Props = {
  items: IItem[]
  total: number
  itemType: ItemType
  loading: boolean
}

export default defineComponent({
  name: 'ItemsTable',
  components: { ReceiptView, MasterAutocomplete },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
    itemType: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props: Props, context: SetupContext) {
    const store = context.root.$store

    const state = reactive<{
      user: Ref<IUser | null>
      receipt: IReceipt | null
      showCategories: Record<number, boolean>
      categoryIds: Record<number, number>
      recommendLists: Record<number, IRecommend[]>
      recommendInputs: Record<number, string>
      matcheds: Record<number, IMatched | null>
    }>({
      user: useVuex<UserState['user']>(
        context,
        () => store.getters['user/user'],
      ),
      receipt: null,
      showCategories: Object.assign(
        {},
        ...props.items.map(item => {
          return { [item.id]: false }
        }),
      ),
      categoryIds: Object.assign(
        {},
        ...props.items.map(item => {
          return { [item.id]: -1 }
        }),
      ),
      recommendLists: Object.assign(
        {},
        ...props.items.map(item => {
          return {
            [item.id]: [],
          }
        }),
      ),
      recommendInputs: Object.assign(
        {},
        ...props.items.map(item => {
          return {
            [item.id]: '',
          }
        }),
      ),
      matcheds: Object.assign(
        {},
        ...props.items.map(item => {
          return { [item.id]: null }
        }),
      ),
    })

    const headers = [
      { text: 'ID', value: 'id', width: 10 },
      { text: 'レシート', value: 'receiptId', align: 'center', width: 10 },
      { text: 'OCR', value: 'ocr', width: 100 },
      { text: '表示名', value: 'name', width: 100 },
      { text: '紐付済商品', value: 'matched', width: 100 },
      { text: '承認', value: 'ok', align: 'center', width: 10 },
      { text: '再マッチング', value: 'reMatch', width: 200 },
      { text: '再紐付', value: 'reOk', align: 'center', width: 10 },
      { text: '削除', value: 'delete', align: 'center', width: 10 },
    ]

    const execRecommendSearch = async (q: string, id: number) => {
      // const categoryId = isRestaurant.value ? 2 : state.categoryIds[index]
      const categoryId = state.categoryIds[id]
      const recommends = (await getRecommends(q, categoryId)).filter(
        rc =>
          rc.pbChainStoreIds === null ||
          rc.pbChainStoreIds.includes(state.receipt?.chainStoreId ?? 0),
      )
      state.recommendLists = {
        ...state.recommendLists,
        [id]: recommends,
      }
    }

    const clearRecommendList = async (id: string) => {
      state.recommendLists = {
        ...state.recommendLists,
        [id]: [],
      }
    }

    const onFocusMasterSelector = (id: number) => {
      state.showCategories = {
        ...state.showCategories,
        [id]: true,
      }
    }

    const onBlurMasterSelector = () => {
      state.showCategories = Object.assign(
        {},
        ...props.items.map(item => {
          return { [item.id]: false }
        }),
      )
    }

    const onSelectRecommend = async (matched: IMatched, id: number) => {
      state.matcheds = {
        ...state.matcheds,
        [id]: matched,
      }
      onBlurMasterSelector()
    }

    const onUnselectRecommend = async (id: number) => {
      state.matcheds = {
        ...state.matcheds,
        [id]: null,
      }
    }

    const onClickReceipt = async (receiptId: number) => {
      if (state.receipt !== null && state.receipt.id === receiptId) {
        // close
        state.receipt = null
        return
      }
      const receipt = await getReceipt(receiptId)
      state.receipt = receipt
    }

    const saveItem = async (item: IItem) => {
      store.dispatch('item/hideItem', item)
      await putItem(item)
    }

    const saveLog = async (item: IItem, approvalResult: ApprovalResult) => {
      if (state.user === null) {
        return
      }
      const now = moment()
      await store.dispatch('log/createLog', {
        logType: LogType.ITEM,
        workedById: state.user.id,
        castId: state.user.castId,
        castName: state.user.castName,
        workerType: state.user.role,
        receiptId: item.receiptId,
        workedAt: now.toDate(),
        workDuration: 0,
        workTarget: 'reItem',
        isModified: false,
        workResult: '',
        approvalResult,
        count: 1,
        isBasicInfoModified: false,
        linkedItems: 0,
        isRewarded: state.user.isRewarded,
        ngReasonFrom: null,
        ngReasonTo: null,
        note: ``,
      })
    }

    const onClickOk = async (item: IItem) => {
      item.newMatchOkedAt = moment().toDate()
      await saveItem(item)
      await saveLog(item, ApprovalResult.APPROVED)
    }

    const onClickReOk = async (item: IItem) => {
      if (state.matcheds[item.id] === null) {
        return
      }
      item.matchedCode = state.matcheds[item.id]?.code || null
      item.matchedName = state.matcheds[item.id]?.name || null
      item.newMatchOkedAt = moment().toDate()
      await saveItem(item)
      await saveLog(item, ApprovalResult.RE_ITEM)
    }

    const onClickDelete = async (item: IItem) => {
      store.dispatch('item/hideItem', item)
      await deleteItem(item)
      await saveLog(item, ApprovalResult.DELETE)
    }

    const isDisableInput = (item: IItem): boolean => {
      return state.recommendInputs[item.id]
        ? state.recommendInputs[item.id].length === 0
        : true
    }

    return {
      state,
      headers,
      onSelectRecommend,
      onUnselectRecommend,
      onFocusMasterSelector,
      onBlurMasterSelector,
      execRecommendSearch,
      clearRecommendList,
      onClickReceipt,
      onClickOk,
      onClickReOk,
      onClickDelete,
      isDisableInput,
    }
  },
})
